/**
 *
 * Classes
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useTranslation } from "react-i18next";

import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { useParams } from "react-router-dom";

import { flushState, loadClasses, updateClass } from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsListFirstElem,
  makeSelectItemsList,
  makeSelectClass,
} from "./selectors";

// antd component
import { Layout, Form } from "antd";
const { Content } = Layout;

//components
import SideList from "components/SideList";
import Preloader from "components/Preloader";
import RenderPopconfirm from "components/RenderPopconfirm";
// inner components
import ClassesDetail from "./containers/ClassesDetail";

// init forms
import { initFormsClasses } from "utils/forms/forms.init";
// helper for forms
import { setFormValues } from "utils/forms/forms.utils";

export function Classes({
  flushState,
  loading,
  history,
  classDetail,
  itemsList,
  firstElem,
  loadClasses,
  updateClass,
  // actions here
}) {
  useInjectReducer({ key: "classes", reducer });
  useInjectSaga({ key: "classes", saga });
  const { t, i18n } = useTranslation();

  const newKey = "new";

  const { id: initId } = useParams();

  const generalForm = useCallback(() => initFormsClasses(), [i18n?.language]);
  // Form initialization for "general" tab
  const [customForm] = Form.useForm();

  const [currentItem, setCurrentItem] = useState(initId);
  const [currentElement, setCurrentElement] = useState(null);
  const [formHaveChanges, setFormHaveChanges] = useState(false);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    !itemsList && loadClasses();
    return () => {
      // Clearing the state after unmounting a component
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Get first element from lists
  // Because history not trigger update component
  useEffect(() => {
    if (initId) {
      setCurrentItem(initId);
    }
  }, [initId, firstElem]);

  const changeCurrentItem = current => {
    setCurrentItem(current);
    history.push({
      pathname: `/classes/${current}`,
      search: "",
      hash: "",
      state: {},
    });
  };
  const addNewItem = () => {
    setCurrentItem(newKey);
    history.push({
      pathname: `/classes/${newKey}`,
    });
  };

  const resetForm = () => customForm.resetFields();
  const submitForm = () => customForm.submit();
  const setFormFieldsValues = values => {
    values && customForm.setFieldsValue(values);
  };
  // update data in the general form
  const setFormData = (formData, genForm = null) => {
    const values = formData || currentElement;
    const mainForm = genForm || generalForm();
    values && setFormValues(customForm, mainForm, values);
  };

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.classes")}</title>
        <meta name="description" content="" />
      </Helmet>
      {loading && !!!itemsList && <Preloader />}
      {!loading && !!itemsList && (
        <>
          <RenderPopconfirm
            placement="right"
            showPopup={formHaveChanges}
            onConfirm={() => {
              submitForm();
              setFormHaveChanges(false);
            }}
            onCancel={() => {
              setFormHaveChanges(false);
              // update data in the general form
              setFormData();
            }}
          >
            <SideList
              className={formHaveChanges ? "preventClass" : ""}
              type="classes"
              title={t("classes.sideListTitle")}
              // subTitle={`${itemsList?.totalCount || 0} classes`}
              sortProps={["title"]}
              searchProps={[
                "title",
                "incumbent.description.firstname",
                "incumbent.description.lastname",
                "students",
              ]}
              filterProps={[
                // 'year',
                {
                  key: "level",
                  label: t("labels.levels"),
                  group: "level",
                },
              ]}
              list={itemsList?.data}
              currentItem={currentItem}
              currentItemAction={changeCurrentItem}
              addAction={addNewItem}
            />
          </RenderPopconfirm>
          <Content className="main-content-container">
            <ClassesDetail
              form={{
                customForm: customForm,
                resetForm: resetForm,
                submitForm: submitForm,
                setFormData: setFormData,
                setFormFieldsValues: setFormFieldsValues,
              }}
              generalForm={generalForm()}
              currentId={currentItem}
              update={updateClass}
              updatedItem={classDetail}
              formHaveChanges={formHaveChanges}
              setFormHaveChanges={setFormHaveChanges}
              currentElement={currentElement}
              setCurrentElement={setCurrentElement}
            />
          </Content>
        </>
      )}
    </Layout>
  );
}

Classes.defaultProps = {
  loading: false,
};
Classes.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
  classDetail: makeSelectClass(),
  firstElem: makeSelectItemsListFirstElem(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadClasses: () => dispatch(loadClasses()),
    updateClass: itemData => dispatch(updateClass(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Classes);
